import { CompanyApi, Configuration, GamesApi, SlotApi } from "../gen/adprovisioning";
import { Game } from "../model/Game";
import { Slot, SLOT_TYPES } from "../model/Slot";
import { getAuthInfo } from "../utility/AccountUtility";
import { deepCopy } from "../utility/DeepCopy";
function convertNetScreenshotToScreenshot(netscreen) {
    return {
        description: netscreen.caption,
        url: netscreen.url
    };
}
function convertScreenshotToNetScreenshot(screenShot) {
    return {
        caption: screenShot.description,
        url: screenShot.url
    };
}
function convertNetGameToGame(net) {
    let r = new Game({
        id: net.id,
        name: net.name,
        developer: net.developerId,
        publisher: net.publisherId,
        screenshots: net.screenshots ? net.screenshots.map(convertNetScreenshotToScreenshot) : [],
        //@ts-ignore
        genre: net.genre,
        //@ts-ignore
        setting: net.setting,
        //@ts-ignore
        style: net.style,
        perspective: net.perspective,
        slots: [],
        iconUrl: null
    });
    return r;
}
function convertNetSlotToSlot(net) {
    let slotType = SLOT_TYPES.TEXTURE;
    if (net.slotType == "object_3d") {
        slotType = SLOT_TYPES.OBJECT_3D;
    }
    else if (net.slotType == "complex") {
        slotType = SLOT_TYPES.COMPLEX;
    }
    return new Slot({
        id: net.id,
        name: net.name,
        gameId: net.game,
        description: net.description,
        prompt: net.prompt,
        type: slotType,
        dimensions: net.dimensions,
        transparency: net.transparency ?? false,
        screenshots: net.screenshots ? net.screenshots.map(convertNetScreenshotToScreenshot) : [],
        slots: net.subslots ? net.subslots.map(convertNetSlotToSlot) : undefined
    });
}
function convertSlotToNetSlot(slot) {
    let result = {
        id: slot.id,
        name: slot.name,
        description: slot.description,
        prompt: slot.prompt,
        slotType: slot.type,
        dimensions: slot.dimensions,
        transparency: slot.transparency,
    };
    if (slot.screenshots) {
        result.screenshots = slot.screenshots.map(convertScreenshotToNetScreenshot);
    }
    if (slot.type == SLOT_TYPES.COMPLEX) {
        result.subslots = slot.slots.map(convertSlotToNetSlot);
    }
    return result;
}
function convertNetApiKeyToApiKey(net) {
    return {
        id: net.id,
        key: net.key,
        active: net.active,
    };
}
export class DefaultMetadataController {
    backendUrl;
    games;
    slots;
    constructor(backendUrl) {
        this.backendUrl = backendUrl;
    }
    // @TODO performance is bad because it will be called unnecessarily often
    sortAll() {
        if (this.games) {
            this.games.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        }
        if (this.slots) {
            this.slots.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        }
    }
    async newSlot(slot) {
        let api = this.getGamesApiClient();
        let netSlot = convertSlotToNetSlot(slot);
        let incomingNetSlot = await api.gameGameIdSlotPost({
            gameId: slot.gameId,
            slot: netSlot
        });
        let incomingSlot = convertNetSlotToSlot(incomingNetSlot);
        this.slots.push(incomingSlot);
        let g = await this.getGame(incomingSlot.gameId);
        g.slots.push(incomingSlot);
        g.slots.sort((a, b) => {
            return a.name.localeCompare(b.name);
        });
        this.sortAll();
        return deepCopy(incomingSlot);
    }
    getSlotApiClient() {
        let authInfo = getAuthInfo();
        return new SlotApi(new Configuration({
            accessToken: authInfo.getToken(),
            basePath: this.backendUrl
        }));
    }
    getGamesApiClient() {
        let authInfo = getAuthInfo();
        return new GamesApi(new Configuration({
            accessToken: authInfo.getToken(),
            basePath: this.backendUrl
        }));
    }
    getCompanyApiClient() {
        let authInfo = getAuthInfo();
        return new CompanyApi(new Configuration({
            accessToken: authInfo.getToken(),
            basePath: this.backendUrl
        }));
    }
    async getAllGames() {
        if (this.games && this.games.length > 0) {
            return deepCopy(this.games);
        }
        let api = this.getGamesApiClient();
        let netGames = await api.gamesGet();
        let games = netGames.map(convertNetGameToGame);
        if (!this.slots || this.slots.length == 0) {
            await this.getAllSlots();
        }
        for (let g of games) {
            let mySlots = this.slots.filter((s) => {
                return s.gameId == g.id;
            });
            g.slots = mySlots;
            g.slots.sort((a, b) => {
                return a.name.localeCompare(b.name);
            });
        }
        this.games = games;
        this.sortAll();
        return deepCopy(games);
    }
    async getAllSlots() {
        let api = this.getSlotApiClient();
        let netSlots = await api.slotsGet();
        let slots = netSlots.map((net) => {
            return convertNetSlotToSlot(net);
        });
        this.slots = slots;
        this.sortAll();
        return deepCopy(slots);
    }
    async getSlot(slotId) {
        if (!(this.slots && this.slots.length > 0)) {
            await this.getAllSlots();
        }
        if (!this.slots) {
            throw "Slot not found";
        }
        let s = this.slots.find((sc) => {
            return sc.id == slotId;
        });
        if (!s) {
            throw "Slot not found";
        }
        return deepCopy(s);
    }
    async getGame(gameId) {
        if (!(this.games && this.games.length > 0)) {
            await this.getAllGames();
        }
        if (!this.games) {
            return null;
        }
        let g = this.games.find((gc) => {
            return gc.id == gameId;
        });
        return deepCopy(g);
    }
    async getApiKeys(gameId) {
        let api = this.getGamesApiClient();
        let netKeys = await api.gameGameIdKeysGet({
            gameId: gameId
        });
        return netKeys.map(convertNetApiKeyToApiKey);
    }
    async invalidateKey(gameId, keyId) {
        let api = this.getGamesApiClient();
        return await api.gameGameIdKeyApiKeyIdInvalidatePut({
            apiKeyId: keyId,
            gameId: gameId
        });
    }
    async generateKey(gameId) {
        let api = this.getGamesApiClient();
        let netKey = await api.gameGameIdKeyGeneratePost({
            gameId: gameId
        });
        return convertNetApiKeyToApiKey(netKey);
    }
    isOwner(game, authUserInfo) {
        return authUserInfo.getCompanyId() == game.developer || authUserInfo.getCompanyId() == game.publisher || authUserInfo.isAdmin();
    }
    async storeSlotData(slot) {
        let api = this.getSlotApiClient();
        let netSlot = convertSlotToNetSlot(slot);
        let incommingNetSlot = await api.slotSlotIdPut({
            slotId: slot.id,
            slot: netSlot,
        });
        const incomingSlot = convertNetSlotToSlot(incommingNetSlot);
        const updateSlots = (slots) => slots.map(cachedSlot => {
            if (cachedSlot.id == incomingSlot.id) {
                return incomingSlot;
            }
            if (cachedSlot.slots) {
                cachedSlot.slots = updateSlots(cachedSlot.slots);
            }
            return cachedSlot;
        });
        if (this.slots) {
            this.slots = updateSlots(this.slots);
        }
        if (this.games) {
            for (const game of this.games) {
                game.slots = updateSlots(game.slots);
                game.slots.sort((a, b) => a.name.localeCompare(b.name));
            }
        }
        this.sortAll();
        return deepCopy(incomingSlot);
    }
}
